/**
 * Regras do arquivo no upload de documentos: PDF, tamanho e competência.
 *
 * Consumido pelo upload individual (aba Arquivos do colaborador) e pelo upload em
 * lote, para que os dois apliquem as mesmas regras.
 */

/** Tamanho máximo do arquivo aceito no upload, em bytes. */
export const TAMANHO_MAXIMO_ARQUIVO = 4 * 1024 * 1024;

export type ISelectCompetencias = {
    anoMaisRecente: number;
    anoMaisAntigo: number;
    meses: number[];
};

export type IResultadoValidacaoArquivo = {
    valido: boolean;
    /** Preenchido quando inválido; pronto para o corpo do toaster. */
    erro?: {
        tipo: 'extensao' | 'tamanho' | 'ausente';
        title: string;
        body: string;
        toasterType: 'error' | 'warning';
    };
};

export function formatBytes(bytes: number, decimals: number = 2): string {
    if (bytes === 0) {
        return '0 Bytes';
    }

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    const valor = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));

    return valor.toLocaleString('pt-BR', { maximumFractionDigits: dm }) + ' ' + sizes[i];
}

export function isPdfFile(file: File): boolean {
    const mimeType = (file.type || '').toLowerCase();
    const fileName = (file.name || '').toLowerCase();

    return mimeType === 'application/pdf' || fileName.endsWith('.pdf');
}

/**
 * Valida o arquivo escolhido. Só PDF, no máximo TAMANHO_MAXIMO_ARQUIVO.
 */
export function validarArquivo(file: File | null | undefined): IResultadoValidacaoArquivo {
    if (!file) {
        return {
            valido: false,
            erro: {
                tipo: 'ausente',
                title: 'Arquivo não informado',
                body: 'Selecione o arquivo que será enviado.',
                toasterType: 'error',
            },
        };
    }

    if (!isPdfFile(file)) {
        return {
            valido: false,
            erro: {
                tipo: 'extensao',
                title: 'Tipo de arquivo inválido',
                body: 'O tipo do arquivo deve ser PDF.',
                toasterType: 'error',
            },
        };
    }

    if (file.size > TAMANHO_MAXIMO_ARQUIVO) {
        return {
            valido: false,
            erro: {
                tipo: 'tamanho',
                title: 'Arquivo maior que permitido',
                body: `O arquivo tem ${formatBytes(file.size)} e o tamanho máximo é `
                    + `${formatBytes(TAMANHO_MAXIMO_ARQUIVO, 0)}.`,
                toasterType: 'warning',
            },
        };
    }

    return { valido: true };
}

/**
 * Converte o arquivo em base64 sem o prefixo data URI, que é o que a API espera
 * no campo `conteudo`.
 */
export function fileToBase64(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            const result = reader.result as string;
            const base64 = result?.split(',')[1];

            if (!base64) {
                reject(new Error('Não foi possível converter o arquivo para base64.'));
                return;
            }

            resolve(base64);
        };

        reader.onerror = () => reject(reader.error || new Error('Erro ao ler arquivo.'));
        reader.readAsDataURL(file);
    });
}

/**
 * Limite da competência selecionável: se hoje já passou da data de fechamento da
 * folha, avança um mês.
 */
export function getLimitDate(moment: any, dataFechamentoFolha: unknown): any {
    const currentDate = moment().startOf('day');
    const competencia = moment(dataFechamentoFolha, 'YYYY-MM-DD');

    if (currentDate.isAfter(competencia)) {
        currentDate.add(1, 'month');
    }

    return currentDate;
}

/** Janela de 12 anos a partir do limite, em ordem decrescente. */
export function montarSelectCompetencias(moment: any, dataFechamentoFolha: unknown): ISelectCompetencias {
    const limitDate = getLimitDate(moment, dataFechamentoFolha);

    return {
        anoMaisRecente: limitDate.year(),
        anoMaisAntigo: limitDate.year() - 11,
        meses: [],
    };
}

/** Meses do ano escolhido; no ano mais recente não oferece mês futuro. */
export function montarMeses(moment: any, dataFechamentoFolha: unknown, year: number): number[] {
    const limitDate = getLimitDate(moment, dataFechamentoFolha);

    if (year < limitDate.year()) {
        return Array.from({ length: 12 }, (_, index) => index + 1);
    }

    return Array.from({ length: limitDate.month() + 1 }, (_, index) => index + 1);
}

/** Limpa o input file no DOM, por id. */
export function limparInputArquivo(inputId: string): void {
    const fileInput = document.getElementById(inputId) as HTMLInputElement | null;
    if (fileInput) {
        fileInput.value = '';
    }
}
